import { computed, inject, Injector } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { type ActivatedRouteSnapshot, type CanActivateChildFn, Router, type RouterStateSnapshot, type UrlTree } from '@angular/router';
import { filter, map, type Observable, take } from 'rxjs';

import { RestaurantsStore } from '#modules/restaurants/stores/restaurants.store';

// A restaurant awaiting its subscription is not paid for yet: only its Platforms page, where its new Google listing gets verified, stays open.
// The restaurant is looked up in the user's list because a guard runs before the parent route resolves it.
export const restaurantAwaitingSubscriptionGuard: CanActivateChildFn = (
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
): Observable<boolean | UrlTree> => {
    const router = inject(Router);
    const injector = inject(Injector);
    const restaurantsStore = inject(RestaurantsStore);

    const restaurantId = childRoute.pathFromRoot.map((route) => route.paramMap.get('restaurant_id')).find((id) => !!id);
    // A list that failed to load does not hold the restaurant back, the views it opens handle the error.
    const isListSettled = computed(() => restaurantsStore.isLoaded() || restaurantsStore.isError());

    return toObservable(isListSettled, { injector }).pipe(
        filter(Boolean),
        take(1),
        map(() => {
            const restaurant = restaurantsStore.restaurants().find(({ id }) => id === restaurantId);
            const platformsUrl = `/restaurants/${restaurantId}/settings/platforms`;
            if (!restaurant?.isAwaitingSubscription() || state.url.startsWith(platformsUrl)) {
                return true;
            }
            return router.createUrlTree(['/restaurants', restaurantId, 'settings', 'platforms', 'connection']);
        })
    );
};
